import classNames from 'classnames';
import * as React from 'react';

import { VoiceboxChat } from 'bones/components/molecules/Voicebox/chat/VoiceboxChat';
import { useVoiceboxConversation } from 'bones/components/molecules/Voicebox/context/VoiceboxConversation';
import { useVoiceboxSchema } from 'bones/components/molecules/Voicebox/context/VoiceboxSchema';
import { useVoiceboxSettings } from 'bones/components/molecules/Voicebox/context/VoiceboxSettings';
import { VoiceboxDescription } from 'bones/components/molecules/Voicebox/shared/VoiceboxDescription';
import { VoiceboxWelcome } from 'bones/components/molecules/Voicebox/shared/VoiceboxWelcome';

export const VoiceboxDrawerBody: React.VFC = () => {
  const { config } = useVoiceboxSchema();
  const {
    isLoadingConversation,
    isLoadingMessage,
    isRefetchingConversation,
    messages,
    sendMessage,
  } = useVoiceboxConversation();
  const {
    catalogProvidersLoading,
    databasesLoading,
    namedGraphsLoading,
    settings,
    lastSettingsLoading,
    lastSettingsRefetching,
  } = useVoiceboxSettings();

  const [aiReasoning, setAiReasoning] = React.useState(
    Boolean(config.initialAiReasoning)
  );
  const [query, setQuery] = React.useState('');
  const [lastTypedQuery, setLastTypedQuery] = React.useState('');
  const [queryHistoryIdx, setQueryHistoryIdx] = React.useState(0);
  const [isReasoningOpenById, setIsReasoningOpenById] = React.useState<
    Record<string, boolean>
  >({});

  const sendMessageDisabled = React.useMemo(
    () =>
      isLoadingConversation ||
      isLoadingMessage ||
      isRefetchingConversation ||
      catalogProvidersLoading ||
      databasesLoading ||
      namedGraphsLoading ||
      lastSettingsLoading ||
      lastSettingsRefetching,
    [
      catalogProvidersLoading,
      databasesLoading,
      isLoadingConversation,
      isLoadingMessage,
      isRefetchingConversation,
      lastSettingsLoading,
      lastSettingsRefetching,
      namedGraphsLoading,
    ]
  );

  const handleSendMessage = React.useCallback(
    (message: string) => {
      const trimmed = message.trim();
      setAiReasoning(false);
      setLastTypedQuery('');
      setQueryHistoryIdx(0);
      setQuery('');
      setIsReasoningOpenById({});
      sendMessage(trimmed, settings, aiReasoning);
    },
    [aiReasoning, sendMessage, settings]
  );

  const isReasoningOpen = React.useCallback(
    (messageId: string | undefined) =>
      isReasoningOpenById[messageId || ''] || false,
    [isReasoningOpenById]
  );

  const setIsReasoningOpen = React.useCallback(
    (messageId: string | undefined) => (isOpen: boolean) => {
      // istanbul ignore else
      if (messageId) {
        setIsReasoningOpenById((prev) => ({
          ...prev,
          [messageId]: isOpen,
        }));
      }
    },
    [setIsReasoningOpenById]
  );

  return (
    <>
      <VoiceboxChat
        aiReasoning={aiReasoning}
        indicator={
          messages.length ? (
            <div className="vb-voicebox-chat-indicator" />
          ) : (
            <div
              className={classNames(
                'vb-voicebox-drawer-info',
                'vb-voicebox-chat-indicator'
              )}
            >
              <VoiceboxWelcome />
              <VoiceboxDescription
                databaseId={settings.databaseId}
                model={settings.model}
                size="sm"
              />
            </div>
          )
        }
        isReasoningOpen={isReasoningOpen}
        lastTypedQuery={lastTypedQuery}
        query={query}
        queryHistoryIdx={queryHistoryIdx}
        sendMessage={handleSendMessage}
        sendMessageDisabled={sendMessageDisabled}
        setAiReasoning={setAiReasoning}
        setIsReasoningOpen={setIsReasoningOpen}
        setLastTypedQuery={setLastTypedQuery}
        setQuery={setQuery}
        setQueryHistoryIdx={setQueryHistoryIdx}
      />
    </>
  );
};
